import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import FileName from './sessionJoinDialogFileName.component';
import SelectFilePair from './sessionJoinDialogSelectFilePair.component';
import ConnectFiles from './sessionJoinDialogSelectHeaders.component';
import ConnectColumns from './selectColumns/sessionJoinDialogSelectColumns.component';

import { addMessage } from '../../../store/actions/message.actions';
import { createVirtualFile } from '../../../store/actions/join.actions';
import { fetchSessions } from '../../../store/actions/session.actions';

const steps = ['Choose name', 'Select files', 'Join Files', 'Choose columns'];

const styles = () => ({
  buttonCell: {
    padding: '25px 0px',
    textAlign: 'center',
  },
  button: {
    float: 'right',
  },
  stepper: {
    width: '100%',
  },
  loadingWrapper: {
    paddingTop: '5%',
    textAlign: 'center',
  },
  progress: {
    margin: '0 auto',
  },
});

const isEmpty = (input) => `${input}`.replace(/\s/g, '') === '';

const filterIncompleteJoins = join => {
  return  !(isEmpty(join.sourceFile) ||
          isEmpty(join.targetFile) ||
          isEmpty(join.sourceHeader) ||
          isEmpty(join.targetHeader))
}

class NewColumnDialogComponent extends React.Component {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.closeDialogAndResetState = this.closeDialogAndResetState.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.saveJoin = this.saveJoin.bind(this);
    this.createJoin = this.createJoin.bind(this);
    this.updateJoin = this.updateJoin.bind(this);
    this.updateColumns = this.updateColumns.bind(this);
    this.state = {
      activeStep: 0,
      name: '',
      joins: [{
        sourceFile: '',
        targetFile: '',
        sourceHeader: '',
        targetHeader: '',
        order: 0,
      }],
      headers: {},
    };
  }


  handleSubmit(event) {
    event.preventDefault();
    this.nextStep();
  }

  nextStep() {
    const { activeStep } = this.state;
    let result = false;

    if (activeStep === 0) {
      result = this.checkName();
    }

    if (activeStep === 1) {
      result = this.checkFiles();
    }

    if (activeStep === 2) {
      result = this.checkHeaders();
    }

    if (activeStep === 3) {
      result = true;
      this.saveJoin();
    }

    if (result) {
      const newStep = this.state.activeStep + 1;
      this.setState({ activeStep: newStep });
    }
  }

  checkName() {
    if (isEmpty(this.state.name)) {
      this.props.addMessage('error', 'Name cannot be empty');
      return false;
    } else if (_.some(this.props.session.files, file => file.filename === this.state.name)) {
      this.props.addMessage('error', 'Name already in use');
      return false;
    }
    return true;
  }

  checkFiles() {
    if (isEmpty(this.state.joins[0].sourceFile) || isEmpty(this.state.joins[0].targetFile)) {
      this.props.addMessage('error', 'At least two files are required');
      return false;
    }
    return true;
  }

  checkHeaders() {
    const joinsWithoutEmptyItem = [...this.state.joins];
    joinsWithoutEmptyItem.pop();
    if (_.some(joinsWithoutEmptyItem, join => isEmpty(join.sourceHeader) || isEmpty(join.targetHeader))) {
      this.props.addMessage('error', 'Not all headers are filled');
      return false;
    }
    return true;
  }

  saveJoin() {
    const { name } = this.state;
    const joins = [...this.state.joins];
    const headersToInclude = this.state.headers;
    this.setState({ processing: true });

    const filteredJoins = joins.filter(filterIncompleteJoins);
    this.props.createVirtualFile(
      this.props.session.id,
      name, filteredJoins,
      headersToInclude
    ).then(() => {
      this.props.fetchSessions();
    });
  }

  closeDialogAndResetState() {
    this.setState({
      activeStep: 0,
      processing: false,
      name: '',
      joins: [{
        sourceFile: '',
        targetFile: '',
        sourceHeader: '',
        targetHeader: '',
        order: 0,
      }],
      headers: {},
    });
    this.props.closeDialog();
  }

  showStep() {
    const { joins, activeStep, name } = this.state;
    const { session, classes } = this.props;

    const filesExcludingVirtual = _.pickBy(session.files, file => !file.virtualFile);

    switch (activeStep) {
      case 0:
        return (
          <FileName
            onChange={filename => this.setState({ name: filename })}
            value={name}
          />
        );
      case 1:
        return joins.map((join, index) => (
          <SelectFilePair
            key={index}
            index={index}
            files={filesExcludingVirtual}
            filteredFiles={_.pickBy(filesExcludingVirtual, file => index === 0 || _.some(joins, j => j.targetFile === file.id || j.sourceFile === file.id))}
            join={join}
            onChange={this.createJoin}
          />
        ));
      case 2:
        return joins.map((join, index) => (!join.targetFile || !join.sourceFile
          ? null
          : <ConnectFiles
            key={index}
            index={index}
            session={this.props.session}
            join={join}
            onChange={this.updateJoin}
          />));
      case 3:
        return (
          <ConnectColumns
            session={this.props.session}
            joins={joins}
            onChange={this.updateColumns}
          />
        );
      case 4:
        return (
          <div className={classes.loadingWrapper}>
            <CircularProgress className={classes.progress} size={50} color="secondary" />
          </div>
        );
      default:
        return 'Unknown step';
    }
  }

  createJoin(index, join) {
    const joins = [...this.state.joins];
    joins[index] = { ...joins[index], ...join };

    if (joins[joins.length - 1].sourceFile && joins[index].sourceFile && joins[index].targetFile) {
      joins[joins.length] = {
        sourceFile: '',
        targetFile: '',
        sourceHeader: '',
        targetHeader: '',
        order: joins.length,
      };
    }

    this.setState({ joins });
  }

  updateJoin(index, join) {
    const joins = [...this.state.joins];
    joins[index] = { ...joins[index], ...join };
    this.setState({ joins });
  }

  updateColumns(newheaders, fileId) {
    const filteredHeaders = Object.keys(newheaders[fileId]).filter(header => newheaders[fileId][header].checked)
      .map(header => newheaders[fileId][header].nameWithFileName);

    this.setState({
      headers: {
        ...this.state.headers,
        [fileId]: filteredHeaders,
      }
    })
  }

  nextOrSaveButton() {
    if (this.state.activeStep >= 3) {
      return (
        <Button
          onClick={() => this.nextStep()}
          color="primary"
          style={{ float: 'right' }}
          disabled={this.state.processing}
        >
          Apply
        </Button>
      );
    }

    return (
      <Button
        onClick={() => this.nextStep()}
        color="primary"
        style={{ float: 'right' }}
      >
        Next
      </Button>
    );
  }

  render() {
    const {
      classes,
      open,
      closeDialog,
    } = this.props;

    const {
      activeStep,
      processing,
    } = this.state;

    return (
      <Dialog
        open={open}
        onClose={() => this.closeDialogAndResetState(closeDialog)}
        maxWidth={false}
      >
        <DialogTitle id="alert-dialog-title">Join Files</DialogTitle>
        <DialogContent className={this.props.classes.dialog}>
          <form onSubmit={this.handleSubmit}>
            {this.showStep()}
          </form>
          <Stepper activeStep={activeStep} className={classes.stepper}>
            {steps.map((label) => {
              const props = {};
              const labelProps = {};
              return (
                <Step key={label} {...props}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          <div className={classes.buttonBlock}>
            <Button
              onClick={() => this.closeDialogAndResetState(closeDialog)}
              color="primary"
              style={{ float: 'left' }}
            >
              {processing ? 'Close' : 'Cancel'}
            </Button>
            {this.nextOrSaveButton()}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

NewColumnDialogComponent.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  closeDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  addMessage: PropTypes.func.isRequired,
  createVirtualFile: PropTypes.func.isRequired,
  fetchSessions: PropTypes.func.isRequired,
  session: PropTypes.shape({
    id: PropTypes.string,
    createdAt: PropTypes.number,
    lastModified: PropTypes.number,
    name: PropTypes.string,
    files: PropTypes.shape({
      id: PropTypes.string,
      filename: PropTypes.string,
      lastModified: PropTypes.number,
      savedByUser: PropTypes.bool,
    }),
  }).isRequired,
  /* eslint-enable */
};

export default withStyles(styles)(connect(
  null,
  { addMessage, createVirtualFile, fetchSessions },
)(NewColumnDialogComponent));
